import { cn } from '@amore/ui/cn'
import { Page, PageBody, PageHeader } from '@amore/ui/components/page'
import type { ReactNode } from 'react'

/**
 * Every planner screen: eyebrow, title, one-line description and at most two
 * header actions (primary last), then the page body. A module with sub-pages
 * passes `SectionTabs` as `tabs`; they replace the header's dividing line.
 */
export function Screen({
  eyebrow = 'Vaše svatba',
  title,
  description,
  actions,
  illustration,
  tabs,
  children,
}: {
  eyebrow?: string
  title: string
  description?: ReactNode
  actions?: ReactNode
  illustration?: ReactNode
  tabs?: ReactNode
  children?: ReactNode
}) {
  const header = (
    <PageHeader
      eyebrow={eyebrow}
      heading={title}
      description={description}
      actions={actions}
      className={cn(tabs && 'border-b-0 pb-5', illustration && 'min-w-0 flex-1 border-b-0 pb-0')}
    />
  )
  return (
    <Page>
      {illustration ? (
        <div
          className={cn('flex items-center gap-3 pb-6 sm:gap-5', !tabs && 'border-b border-border')}
        >
          <div aria-hidden="true" className="w-16 shrink-0 sm:w-24">
            {illustration}
          </div>
          {header}
        </div>
      ) : (
        header
      )}
      {tabs}
      <PageBody className="gap-6">{children}</PageBody>
    </Page>
  )
}

/** A titled block inside a screen: heading, optional description and one action. */
export function ScreenSection({
  title,
  description,
  action,
  children,
}: {
  title: string
  description?: ReactNode
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h2 className="font-heading text-2xl leading-tight tracking-tight">{title}</h2>
          {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {action}
      </div>
      {children}
    </section>
  )
}
